/**
 * The visual identity of each restaurant theme, as the gallery card shows it.
 *
 * Transcribed verbatim from `Restaurant Themes.dc.html`'s own script — the
 * `themes` array carries a `stage` gradient, an accent and its ink, a display
 * font and three swatches per family. Those are DESIGN facts about the theme,
 * not storefront tokens: the card is a poster for a look the seller has not
 * applied yet, so it cannot read `--store-*` (those belong to whatever theme IS
 * applied) and the values have to live somewhere on this side.
 *
 * Keyed by `family`, which is also the `layout` token the buyer switches on, so
 * a theme whose family is unknown here still renders — it just falls back to
 * the portal's own surface instead of carrying a gradient.
 *
 * ## The stage: a WEBSITE and an APP
 *
 * The design's stage is not one flat picture. It is a desktop frame and a phone
 * frame standing on the gradient — because that is the actual claim being made
 * to a seller: one theme dresses both the site and the dine-in app. A single
 * screenshot of the website says half of it.
 *
 * Both shots live in this app's `public/`, captured from the real storefront.
 * The design offers a live-preview mode that renders the artboards in iframes
 * instead; the iframes are deliberately not reproduced. Six of them on one
 * screen is six full page loads for a decision made from a picture, and a
 * preview of the ARTBOARD would drift from what the theme actually renders. A
 * screenshot of our own storefront cannot.
 */
export interface ThemeCardIdentity {
  /** The stage behind the thumbnail — visible while the image loads. */
  stage: string;
  /** The badge ground. */
  accent: string;
  /** Ink ON the badge: white on STACK's red, near-black on KOYO's gold. */
  accentInk: string;
  /** The badge's letter is set in the theme's own display face. */
  displayFont: string;
  initial: string;
  /** Three dots: the palette in miniature. */
  swatches: [string, string, string];
  /**
   * Set on the three TABLE-APP themes (calm / photos / tiles, 2026-10-20):
   * the shot name under `public/table-app-thumbnails/`. Those designs have no
   * website and no website sections, so the card offers no `Customize`.
   */
  tableAppShot?: "calm" | "gallery" | "tiles";
}

export const THEME_CARDS: Record<string, ThemeCardIdentity> = {
  /* The table-app themes (founder, 2026-10-20: "treat the 3 as 3 themes like
     homegrill and stack"). Stages and badges are the ones their cards had
     when they were a separate "Menu style at the table" row. */
  calm: {
    stage: "rgb(241,241,239)",
    accent: "rgb(55,53,47)",
    accentInk: "#fff",
    displayFont: "'Inter', sans-serif",
    initial: "A",
    swatches: ["#37352F", "#F1F1EF", "#FFFFFF"],
    tableAppShot: "calm",
  },
  photos: {
    stage: "rgb(232,241,250)",
    accent: "rgb(35,131,226)",
    accentInk: "#fff",
    displayFont: "'Inter', sans-serif",
    initial: "B",
    swatches: ["#2383E2", "#E8F1FA", "#37352F"],
    tableAppShot: "gallery",
  },
  tiles: {
    stage: "rgb(251,243,219)",
    accent: "rgb(203,145,47)",
    accentInk: "#fff",
    displayFont: "'Inter', sans-serif",
    initial: "C",
    swatches: ["#CB912F", "#FBF3DB", "#37352F"],
    tableAppShot: "tiles",
  },
  stack: {
    stage: "linear-gradient(135deg,#E8DFCE,#DCCFB6)",
    accent: "#D92B2B",
    accentInk: "#fff",
    displayFont: "'Archivo Black', sans-serif",
    initial: "S",
    swatches: ["#D92B2B", "#FFD23F", "#1B1512"],
  },
  homegrill: {
    stage: "linear-gradient(135deg,#FFE9A8,#FFD65C)",
    accent: "#FFC72C",
    accentInk: "#1D1D1F",
    displayFont: "'Plus Jakarta Sans', sans-serif",
    initial: "H",
    swatches: ["#FFC72C", "#1D1D1F", "#DA291C"],
  },
  verde: {
    stage: "linear-gradient(135deg,#DCE8DC,#BFD4BF)",
    accent: "#1E5C3F",
    accentInk: "#fff",
    displayFont: "'Plus Jakarta Sans', sans-serif",
    initial: "V",
    swatches: ["#1E5C3F", "#9BBF9E", "#F4C543"],
  },
  koyo: {
    stage: "linear-gradient(135deg,#2A2A2E,#101012)",
    accent: "#C6A15B",
    accentInk: "#0E0E10",
    displayFont: "'Plus Jakarta Sans', sans-serif",
    initial: "K",
    swatches: ["#0E0E10", "#C6A15B", "#ECEAE4"],
  },
  otto: {
    stage: "linear-gradient(135deg,#E9E5D6,#D6CFBA)",
    accent: "#1F4A33",
    accentInk: "#fff",
    displayFont: "'Libre Caslon Text', serif",
    initial: "O",
    swatches: ["#1F4A33", "#F7F6F1", "#B4832F"],
  },
  bru: {
    stage: "linear-gradient(135deg,#DCE9E3,#B9D2C6)",
    accent: "#056443",
    accentInk: "#fff",
    displayFont: "'Plus Jakarta Sans', sans-serif",
    initial: "B",
    swatches: ["#056443", "#D4E9E2", "#1E1E1E"],
  },
};

/**
 * The two shots a stage is built from, or `null` when the family is unknown.
 *
 * Both are captured from the real storefront with the demo-catalog switcher and
 * the preview-only badges hidden — a "not live yet" chip inside a theme's
 * portrait reads as a fault in the theme rather than a note about one unbuilt
 * feature.
 *
 * Captured at 1472×1200 and 390×818, then downscaled to exactly 2× the frames
 * they land in (736×600 and 254×532) and encoded as WebP. Shipping the raw PNGs
 * meant 7.3 MB in the repo for pictures drawn at 368px wide.
 */
export function themeShots(
  family: string,
): { web: string; app: string } | null {
  if (!Object.hasOwn(THEME_CARDS, family)) return null;
  const tableAppShot = THEME_CARDS[family].tableAppShot;
  if (tableAppShot)
    return {
      web: `/table-app-thumbnails/${tableAppShot}-web.webp`,
      app: `/table-app-thumbnails/${tableAppShot}-app.webp`,
    };
  return {
    web: `/theme-thumbnails/${family}-web.webp`,
    app: `/theme-thumbnails/${family}-app.webp`,
  };
}
